import React from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
} from "reactstrap";
import classnames from "classnames";
import { ToastContainer } from "react-toastify";
import SimpleHeader from "@/components/Headers/SimpleHeader";


import PasswordModalContainer from "@/layouts/PasswordModalContainer";

import ImageUploadContainer from "./User/adding-user/ImageUploadContainer";
import type {
  ImageUploadContainerProps,
  PickedImage,
} from "./User/adding-user/ImageUploadContainer";

/** The stored avatar URL, or the image just picked in the form. */
type Avatar = string | PickedImage | null;

export type ProfileViewProps = {
  name: string;
  email: string;
  role?: string;
  avatar?: Avatar;
  errors?: Record<string, unknown>;
  loading?: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onImagePicked: ImageUploadContainerProps["ImageUpload"];
  saveDisabled?: boolean;
};

/**
 * What the avatar card is handed: the stored URL, a media pick's `secure_url`,
 * or a picked file's data URL. Until SCRUM-2111 the picked file object itself was
 * passed off as a string through a double cast; ImageUploadContainer ignores `image`
 * while it holds a picked file of its own (its `imgObject.data`), which is the
 * only way such an object reaches this view, so what renders is unchanged.
 */
const avatarImage = (avatar: Avatar | undefined): string | null => {
  if (!avatar) return null;
  if (typeof avatar === "string") return avatar;
  return avatar.secure_url || avatar.data || null;
};

const ProfileView = ({
  name,
  email,
  role,
  avatar,
  errors,
  loading,
  onChange,
  onSubmit,
  onImagePicked,
  saveDisabled = false,
}: ProfileViewProps) => {
  // ProfileContainer keeps the server's field errors untyped; the name error is a message.
  const nameError = errors?.name as React.ReactNode;
  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <SimpleHeader name="Update profile" parentName="Profile" />

      <Container className="mt--6" fluid>
        <Row>
          <Col className="order-xl-2" xl="4">
            <Card className="card-pricing border-0 text-center mb-4">
              <CardHeader className="bg-gradient-primary">
                <h4 className="text-uppercase ls-0 text-white py-2 mb-0">
                  Update Avatar
                </h4>
              </CardHeader>
              <CardBody className="px-lg-2">
                <ImageUploadContainer
                  ImageUpload={onImagePicked}
                  image={avatarImage(avatar)}
                />
              </CardBody>
            </Card>
          </Col>

          <Col className="order-xl-1" xl="8">
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">Edit profile</h3>
                  </Col>
                  <Col className="text-right" xs="4">
                    <PasswordModalContainer />
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Form onSubmit={onSubmit}>
                  <h6 className="heading-small text-muted mb-4">
                    User information
                  </h6>

                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-username"
                          >
                            Username
                          </label>
                          <Input
                            id="input-username"
                            placeholder="Username"
                            type="text"
                            value={name}
                            onChange={onChange}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-email"
                          >
                            Email address
                          </label>
                          <Input
                            id="input-email"
                            placeholder="jesse@example.com"
                            name="email"
                            type="email"
                            value={email}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-full-name"
                          >
                            Full name
                          </label>
                          <Input
                            id="input-full-name"
                            placeholder="Full name"
                            name="name"
                            type="text"
                            className={classnames("form-control mt-2", {
                              "is-invalid": nameError,
                            })}
                            value={name}
                            onChange={onChange}
                          />
                          {nameError && (
                            <div className="invalid-feedback">
                              {nameError}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  <hr className="my-4" />

                  {/* SCRUM-1024: when the profile never loaded, the empty
                      fields are not the stored values — saving would blank the
                      real record. */}
                  {saveDisabled && (
                    <p className="text-danger small mb-3">
                      Your profile could not be loaded, so it cannot be saved
                      right now. Please reload the page.
                    </p>
                  )}
                  <Button
                    color="primary"
                    type="submit"
                    disabled={loading || saveDisabled}
                  >
                    {loading ? "Uploading..." : "Edit"}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ProfileView;
